import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  Zap, TrendingUp, Bot, ShieldCheck, BarChart3, ArrowUpRight,
  AlertTriangle, Check, ChevronDown, Target, Gauge, Lock, TerminalSquare, Flame,
  MousePointerClick, PhoneCall, Rocket, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

const BLUE = "#2E6BFF";
const EMERALD = "#10B981";
const AMBER = "#F59E0B";

const METRICS = [
  { stat: "38%", label: "Avg Conversion Lift" },
  { stat: "14d", label: "Median Deployment" },
  { stat: "$4.2M+", label: "Client Revenue Tracked" },
  { stat: "0", label: "Template Themes Shipped" },
];

const PAINS = [
  { title: "Template sites bleed trust", desc: "Buyers smell a page builder in three seconds. If your site looks rented, your pricing gets negotiated like it." },
  { title: "Slow loads kill checkout", desc: "Every extra second of load time burns double-digit conversion. Speed isn't polish — it's pipeline." },
  { title: "No telemetry, no decisions", desc: "If you can't see which page loses the deal, you're funding guesses. Instrumentation is not optional." },
];

const STACK = [
  { id: "conversion", icon: Target, title: "Conversion Architecture", desc: "Every section is engineered around one job: moving a visitor to revenue. Offer hierarchy, friction audits, CTA choreography." },
  { id: "automation", icon: Bot, title: "AI Automation Layer", desc: "Qualification, follow-up drafts and proposal generation run while you sleep. Your pipeline works weekends." },
  { id: "telemetry", icon: BarChart3, title: "Revenue Telemetry", desc: "Funnel dashboards wired to the dollar. Know exactly which visit became which invoice — and why." },
  { id: "security", icon: ShieldCheck, title: "Security & Uptime", desc: "Hardened auth, encrypted storage, monitored deploys. Enterprise buyers check. We make sure you pass." },
];

const STEPS = [
  { n: "01", title: "Revenue Diagnostic", desc: "We map where your current funnel leaks money and quantify the cost of every friction point." },
  { n: "02", title: "Architecture", desc: "Page hierarchy, offer stack, instrumentation plan — signed off before a single pixel ships." },
  { n: "03", title: "Build Sprint", desc: "Fixed-scope sprint. Daily staging links, milestone telemetry, no black-box development." },
  { n: "04", title: "Launch & Optimize", desc: "Deploy, measure, tighten. The engine compounds — conversion is a system, not an event." },
];

const ENGAGEMENTS = [
  {
    name: "Flat-Rate Deployment Special",
    badge: "Limited Time",
    price: "$450",
    strike: "normally from $7.5k",
    note: "The full Commercial Revenue Engine — conversion architecture, AI automation layer and revenue telemetry — at one flat rate. No hourly meters, no scope creep. When this deployment window closes, standard sprint pricing resumes.",
    accent: AMBER,
    href: "/terminal",
    cta: "Claim the $450 Special",
  },
  {
    name: "Revenue Retainer",
    price: "from $3.5k/mo",
    note: "Continuous optimization, AI automation upgrades and monthly telemetry reviews. For operators who want the engine tuned every single month.",
    accent: EMERALD,
    href: "/pricing",
    cta: "See Retainers",
  },
];

const FAQS = [
  { q: "What's the catch with the $450 flat-rate special?", a: "There isn't one. It's a limited-time rate to fill this deployment window — same principal engineers, same fixed-scope protocol, same telemetry instrumentation. When the window closes, standard sprint pricing (from $7.5k) resumes." },
  { q: "How is this different from hiring a marketing agency?", a: "Agencies rent you attention. We build the machine that converts it — owned infrastructure, instrumented end to end, no recurring ad-spend dependency." },
  { q: "What does 'fixed-scope sprint' actually mean?", a: "A written scope, a fixed price, a staging link from day two. If it's not in scope, we quote it separately. No hourly meters running in the dark." },
  { q: "Do you work with our existing stack?", a: "Yes. We integrate with your CRM, payment rails and analytics. If a component can't pay for itself, we tell you to cut it." },
  { q: "What happens after launch?", a: "The telemetry layer tells us exactly where revenue stalls. Retainer clients get monthly optimization sprints against those numbers — not opinions." },
];

const DEALS = [
  { name: "DW Investment Group", value: "$18,400", status: "WON", tone: EMERALD },
  { name: "Meridian Logistics", value: "$12,750", status: "WON", tone: EMERALD },
  { name: "Atlas Wellness Co.", value: "$9,200", status: "PROPOSAL", tone: AMBER },
  { name: "Copperline HVAC", value: "$7,500", status: "QUALIFYING", tone: BLUE },
];

export default function LaunchLanding() {
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);
  const [barsOn, setBarsOn] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [buyBusy, setBuyBusy] = useState(false);

  const buyNowPayLater = async () => {
    if (buyBusy) return;
    setBuyBusy(true);
    try {
      const r = await api.post("/payments/launch-special", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error("Checkout failed to start — try the terminal instead.");
      setBuyBusy(false);
    }
  };

  useEffect(() => {
    let alive = true;
    api.get("/settings/feature-flags")
      .then((r) => { if (alive) setAllowed(r.data?.launch_page_enabled !== false); })
      .catch(() => { if (alive) setAllowed(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (allowed === false) navigate("/", { replace: true });
  }, [allowed, navigate]);

  useEffect(() => {
    if (allowed) {
      const t = setTimeout(() => setBarsOn(true), 400);
      return () => clearTimeout(t);
    }
  }, [allowed]);

  if (allowed !== true) {
    return (
      <div className="min-h-screen bg-[#090C11] flex items-center justify-center" data-testid="launch-loading">
        <div className="font-jakarta text-xs uppercase tracking-[0.35em] text-[#2E6BFF] animate-pulse">Spinning Up Engine…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090C11] text-[#E8EBF0] font-jakarta" data-testid="launch-page">
      {/* ============ LIMITED-TIME SPECIAL BANNER ============ */}
      <div className="bg-[#F59E0B] text-[#090C11] border-b-4 border-[#090C11]" data-testid="launch-special-banner">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-5 lg:py-6 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center gap-2 text-sm lg:text-base font-extrabold uppercase tracking-[0.18em]">
              <Flame size={16} strokeWidth={2.5} /> Limited Time Offer — $450 Flat Rate
            </div>
            <div className="mt-1.5 text-xs lg:text-sm font-semibold tracking-wide text-[#090C11]/80">
              Full commercial deployment: conversion architecture · AI automation · revenue telemetry — <span className="line-through">normally from $7,500</span>
            </div>
          </div>
          <Link
            to="/terminal"
            data-testid="launch-special-banner-cta"
            className="shrink-0 inline-flex items-center gap-2 bg-[#090C11] text-[#F59E0B] px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-extrabold hover:bg-[#1a2230] transition-colors duration-300"
          >
            Claim Your Slot <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>

      {/* ============ HEADER ============ */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#090C11]/85 border-b border-[#2E6BFF]/15">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="launch-header-brand">
            <span className="font-extrabold tracking-tight text-lg">Trace<span className="text-[#2E6BFF]">Elements</span></span>
            <span className="hidden sm:inline-block h-4 w-px bg-[#2E6BFF]/30" />
            <span className="hidden sm:inline-block text-[10px] uppercase tracking-[0.3em] text-[#E8EBF0]/60">Commercial Revenue Engine</span>
          </Link>
          <Link
            to="/terminal"
            data-testid="launch-header-cta"
            className="inline-flex items-center gap-2 bg-[#2E6BFF] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[#4d84ff] hover:shadow-[0_0_24px_rgba(46,107,255,0.45)] transition-all duration-300"
          >
            Initialize Build <ArrowUpRight size={13} />
          </Link>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden border-b border-[#2E6BFF]/10" data-testid="launch-hero">
        <div className="absolute inset-0 pointer-events-none opacity-[0.05]"
          style={{ backgroundImage: "linear-gradient(#2E6BFF 1px, transparent 1px), linear-gradient(90deg, #2E6BFF 1px, transparent 1px)", backgroundSize: "56px 56px" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#090C11] pointer-events-none" />
        <div className="relative max-w-[1240px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <div className="flex flex-wrap items-center gap-3">
              <div
                className="inline-flex items-center gap-2 border border-[#2E6BFF] text-[#2E6BFF] px-4 py-2 text-[10px] uppercase tracking-[0.28em] font-semibold"
                data-testid="launch-hero-badge"
              >
                <Zap size={13} /> Revenue Infrastructure · Commercial Deployment
              </div>
              <div
                className="inline-flex items-center gap-1.5 bg-[#F59E0B] text-[#090C11] px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-extrabold"
                data-testid="launch-hero-special-pill"
              >
                <Flame size={12} /> $450 Flat Rate — Limited Time
              </div>
            </div>
            <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]" data-testid="launch-hero-headline">
              Your Website Should<br /><span className="text-[#2E6BFF]">Close Deals.</span><br />Not Just Sit There.
            </h1>
            <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#E8EBF0]/70 max-w-xl" data-testid="launch-hero-subcopy">
              We build conversion-weaponized web infrastructure for operators who measure everything.
              Engineered funnels, AI automation, telemetry to the dollar — deployed in weeks, not quarters.
              <span className="block mt-3 text-[#F59E0B] font-semibold text-base">This deployment window: the full engine at a $450 flat rate.</span>
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/terminal"
                data-testid="launch-hero-cta"
                className="inline-flex items-center gap-2 bg-[#2E6BFF] text-white px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#4d84ff] hover:shadow-[0_0_28px_rgba(46,107,255,0.5)] transition-all duration-300"
              >
                Claim the $450 Special <ArrowUpRight size={15} />
              </Link>
              <Link
                to="/work"
                data-testid="launch-hero-cta-work"
                className="inline-flex items-center gap-2 border border-[#E8EBF0]/25 text-[#E8EBF0]/80 px-7 py-4 text-xs uppercase tracking-[0.22em] hover:border-[#10B981]/60 hover:text-[#10B981] transition-colors duration-300"
              >
                See the Fleet
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#E8EBF0]/40">
              <TerminalSquare size={13} className="text-[#10B981]" /> Fixed scope · Fixed price · Staging link from day two
            </div>
          </div>

          {/* Revenue console mock */}
          <div className="lg:col-span-6">
            <div className="border border-[#2E6BFF]/25 bg-[#0E1219] shadow-[0_30px_80px_rgba(0,0,0,0.5)]" data-testid="launch-revenue-console">
              <div className="flex items-center justify-between px-5 py-3 border-b border-[#E8EBF0]/10">
                <span className="text-[10px] uppercase tracking-[0.28em] text-[#E8EBF0]/50">Revenue Console</span>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#10B981]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] animate-pulse" /> Live Mock
                </span>
              </div>

              <div className="grid grid-cols-3 divide-x divide-[#E8EBF0]/10 border-b border-[#E8EBF0]/10">
                {[
                  { k: "Pipeline", v: "$49.8k" },
                  { k: "Close Rate", v: "34%" },
                  { k: "Avg Deal", v: "$12.4k" },
                ].map((s) => (
                  <div key={s.k} className="px-4 py-4 text-center">
                    <div className="text-xl font-extrabold text-[#E8EBF0]">{s.v}</div>
                    <div className="mt-1 text-[9px] uppercase tracking-[0.22em] text-[#E8EBF0]/45">{s.k}</div>
                  </div>
                ))}
              </div>

              {/* Conversion bars */}
              <div className="px-5 py-5 border-b border-[#E8EBF0]/10">
                <div className="flex items-baseline justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#E8EBF0]/50">Weekly Attributed Revenue</span>
                  <span className="inline-flex items-center gap-1 text-xs font-bold text-[#10B981]"><TrendingUp size={13} /> +38%</span>
                </div>
                <div className="flex items-end gap-2 h-24" data-testid="launch-chart">
                  {[32, 45, 38, 58, 52, 71, 66, 84, 78, 95].map((h, i) => (
                    <div key={i} className="flex-1 bg-gradient-to-t from-[#2E6BFF]/30 to-[#2E6BFF] transition-all duration-700"
                      style={{ height: barsOn ? `${h}%` : "4%", transitionDelay: `${i * 60}ms`, opacity: i > 6 ? 1 : 0.65 }} />
                  ))}
                </div>
              </div>

              {/* Deal rows */}
              <div className="px-5 py-4">
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#E8EBF0]/50 mb-3">Deal Flow · This Week</div>
                <div className="space-y-2.5">
                  {DEALS.map((d) => (
                    <div key={d.name} className="flex items-center gap-3">
                      <span className="text-sm text-[#E8EBF0]/80 flex-1 truncate">{d.name}</span>
                      <span className="text-sm font-bold text-[#E8EBF0]">{d.value}</span>
                      <span className="text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-1 border" style={{ color: d.tone, borderColor: `${d.tone}55`, backgroundColor: `${d.tone}14` }}>
                        {d.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ METRICS RIBBON ============ */}
      <section className="bg-[#0F1520] border-b border-[#2E6BFF]/15" data-testid="launch-metrics-ribbon">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 lg:grid-cols-4 gap-10">
          {METRICS.map((m) => (
            <div key={m.label} className="text-center lg:text-left" data-testid={`launch-metric-${m.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="text-4xl lg:text-5xl font-extrabold text-[#2E6BFF] tracking-tight">{m.stat}</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.28em] text-[#E8EBF0]/55">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ EASY ONBOARDING ============ */}
      <section className="py-20 lg:py-28 border-b border-[#2E6BFF]/10 bg-[#0B0F16]" data-testid="launch-onboarding">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#2E6BFF] mb-4">// Effortless Onboarding</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="launch-onboarding-heading">
            Started in Minutes. <span className="text-[#E8EBF0]/40">Not Meetings.</span>
          </h2>
          <p className="mt-4 text-[#E8EBF0]/60 max-w-xl">
            No discovery-call gauntlet. No 40-page brief. Three moves and your build is underway.
          </p>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { icon: MousePointerClick, title: "1 · Claim Your Slot", desc: "Two minutes in the project terminal: three sentences about your business and the outcome you want. That's the entire intake." },
              { icon: PhoneCall, title: "2 · Same-Day Asset Handover", desc: "One 20-minute call and a shared drive for your logo, photos and copy. After that, you never have to think about the build again." },
              { icon: Rocket, title: "3 · Staging Link From Day Two", desc: "You watch the build live from a private staging URL. Median time from handover to launched: 14 days." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="border border-[#E8EBF0]/10 hover:border-[#2E6BFF]/50 bg-[#090C11] p-7 transition-colors duration-300" data-testid={`onboarding-step-${i}`}>
                  <span className="h-11 w-11 border border-[#2E6BFF]/40 bg-[#2E6BFF]/10 flex items-center justify-center text-[#2E6BFF] mb-5">
                    <Icon size={18} />
                  </span>
                  <h3 className="text-lg font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-[#E8EBF0]/55">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ PROBLEM ============ */}
      <section className="py-20 lg:py-28 border-b border-[#2E6BFF]/10" data-testid="launch-problem">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#F59E0B] mb-4">// The Cost-Center Problem</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight max-w-3xl" data-testid="launch-problem-heading">
            Most business websites are expensive brochures. <span className="text-[#E8EBF0]/40">Yours shouldn't be.</span>
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PAINS.map((p, i) => (
              <div key={p.title} className="border border-[#E8EBF0]/10 hover:border-[#F59E0B]/50 bg-[#0E1219] p-6 transition-colors duration-300" data-testid={`launch-pain-${i}`}>
                <span className="h-11 w-11 border border-[#F59E0B]/40 bg-[#F59E0B]/10 flex items-center justify-center text-[#F59E0B] mb-5">
                  <AlertTriangle size={18} />
                </span>
                <h3 className="text-lg font-bold tracking-tight">{p.title}</h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-[#E8EBF0]/55">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ THE STACK ============ */}
      <section className="py-20 lg:py-28 border-b border-[#2E6BFF]/10" data-testid="launch-stack">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#2E6BFF] mb-4">// The Engine Stack</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight max-w-2xl" data-testid="launch-stack-heading">
            Four Systems. One Revenue Machine.
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {STACK.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.id} className="group border border-[#E8EBF0]/10 hover:border-[#10B981]/50 bg-[#0E1219] p-6 transition-colors duration-300" data-testid={`launch-stack-${s.id}`}>
                  <span className="h-11 w-11 border border-[#10B981]/40 bg-[#10B981]/10 flex items-center justify-center text-[#10B981] mb-5">
                    <Icon size={18} />
                  </span>
                  <h3 className="text-lg font-bold tracking-tight">{s.title}</h3>
                  <p className="mt-2.5 text-[13px] leading-relaxed text-[#E8EBF0]/55">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section className="py-20 lg:py-28 border-b border-[#2E6BFF]/10 bg-[#0B0F16]" data-testid="launch-process">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#2E6BFF] mb-4">// Deployment Protocol</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="launch-process-heading">Four Moves to Revenue.</h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div key={s.n} className="relative border border-[#E8EBF0]/10 bg-[#090C11] p-6" data-testid={`launch-step-${i}`}>
                <div className="text-5xl font-extrabold tracking-tighter text-[#2E6BFF]/25">{s.n}</div>
                <h3 className="mt-4 text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2.5 text-[13px] leading-relaxed text-[#E8EBF0]/55">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <span className="hidden xl:block absolute top-1/2 -right-3.5 text-[#2E6BFF]/50 z-10"><ArrowUpRight size={16} /></span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ENGAGEMENTS ============ */}
      <section className="py-20 lg:py-28 border-b border-[#2E6BFF]/10" data-testid="launch-engagements">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#2E6BFF] mb-4">// Engagement Models</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="launch-engagements-heading">
            One Flat Rate. <span className="text-[#F59E0B]">Limited Time.</span>
          </h2>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            {ENGAGEMENTS.map((e) => (
              <div key={e.name} className="relative border bg-[#0E1219] p-8 lg:p-10 transition-colors duration-300" style={{ borderColor: `${e.accent}66` }} data-testid={`launch-engagement-${e.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {e.badge && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-[#F59E0B] text-[#090C11] px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-extrabold" data-testid="launch-special-badge">
                    <Flame size={11} /> {e.badge}
                  </span>
                )}
                <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: e.accent }}>{e.name}</div>
                <div className="mt-4 flex items-end gap-3 flex-wrap">
                  <span className="text-4xl lg:text-5xl font-extrabold tracking-tighter">{e.price}</span>
                  {e.strike && (
                    <span className="pb-1.5 text-sm text-[#E8EBF0]/40 line-through" data-testid="launch-special-strike">{e.strike}</span>
                  )}
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#E8EBF0]/60 max-w-md">{e.note}</p>
                {e.badge && (
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-[#10B981]" data-testid="launch-special-bnpl-note">
                    <CalendarClock size={14} /> or 4 interest-free payments of $112.50 — Klarna / Afterpay at checkout
                  </div>
                )}
                <Link
                  to={e.href}
                  data-testid={`launch-engagement-cta-${e.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 text-[11px] uppercase tracking-[0.22em] font-extrabold transition-all duration-300"
                  style={e.badge
                    ? { backgroundColor: e.accent, color: "#090C11" }
                    : { border: `1px solid ${e.accent}`, color: e.accent }}
                >
                  {e.cta} <ArrowUpRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ BUY NOW, PAY LATER ============ */}
      <section className="py-16 lg:py-20 border-b border-[#10B981]/25 bg-gradient-to-r from-[#10B981]/10 via-[#0E1219] to-[#0E1219]" data-testid="launch-bnpl">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#10B981] text-[#090C11] px-4 py-2 text-[11px] uppercase tracking-[0.24em] font-extrabold" data-testid="launch-bnpl-badge">
              <CalendarClock size={14} strokeWidth={2.5} /> Buy Now, Pay Later
            </div>
            <h2 className="mt-6 text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="launch-bnpl-heading">
              So Your Development <span className="text-[#10B981]">Never Stops.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#E8EBF0]/70 max-w-xl">
              Cash flow shouldn't decide your launch date. Split the <span className="font-bold text-[#E8EBF0]">$450 special</span> into
              <span className="font-bold text-[#10B981]"> 4 interest-free payments of $112.50</span> with Klarna or Afterpay at checkout —
              your build starts immediately while the payments spread out.
            </p>
            <div className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#E8EBF0]/40">
              No credit impact to check eligibility · All major cards also accepted
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-[#10B981]/50 bg-[#090C11] p-8 text-center">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8EBF0]/45">Today's Special</div>
              <div className="mt-3 flex items-end justify-center gap-3">
                <span className="text-5xl font-extrabold tracking-tighter">$450</span>
                <span className="pb-1.5 text-sm text-[#E8EBF0]/40 line-through">$7,500</span>
              </div>
              <div className="mt-2 text-sm font-bold text-[#10B981]" data-testid="launch-bnpl-split">4 × $112.50 interest-free</div>
              <button
                onClick={buyNowPayLater}
                disabled={buyBusy}
                data-testid="launch-bnpl-cta"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#10B981] text-[#090C11] px-8 py-4 text-xs uppercase tracking-[0.22em] font-extrabold hover:bg-[#34d399] hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] transition-all duration-300 disabled:opacity-60"
              >
                {buyBusy ? "Opening Secure Checkout…" : "Buy Now — Pay Later"} <ArrowUpRight size={15} />
              </button>
              <div className="mt-4 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#E8EBF0]/40">
                <Lock size={11} className="text-[#10B981]" /> Secure checkout by Stripe · Klarna · Afterpay · Affirm
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="py-20 lg:py-28 border-b border-[#2E6BFF]/10" data-testid="launch-faq">
        <div className="max-w-[860px] mx-auto px-6 lg:px-10">
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#2E6BFF] mb-4">// Objection Handling</div>
          <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="launch-faq-heading">Straight Answers.</h2>
          <div className="mt-12 divide-y divide-[#E8EBF0]/10 border-y border-[#E8EBF0]/10">
            {FAQS.map((f, i) => (
              <div key={f.q}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`launch-faq-q-${i}`}
                  className="w-full flex items-center justify-between gap-4 py-6 text-left group"
                >
                  <span className="text-base lg:text-lg font-bold tracking-tight group-hover:text-[#2E6BFF] transition-colors">{f.q}</span>
                  <ChevronDown size={18} className={`shrink-0 text-[#2E6BFF] transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <p className="pb-6 text-sm leading-relaxed text-[#E8EBF0]/60 max-w-2xl" data-testid={`launch-faq-a-${i}`}>{f.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 lg:py-28" data-testid="launch-final-cta">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10">
          <div className="relative bg-[#0E1219] border border-[#2E6BFF]/40 p-10 lg:p-16 text-center">
            <span className="absolute top-0 left-0 h-10 w-10 border-t-2 border-l-2 border-[#2E6BFF]" />
            <span className="absolute bottom-0 right-0 h-10 w-10 border-b-2 border-r-2 border-[#2E6BFF]" />
            <div className="inline-flex items-center gap-1.5 bg-[#F59E0B] text-[#090C11] px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-extrabold mb-8" data-testid="launch-final-special-pill">
              <Flame size={12} /> Limited Time — $450 Flat Rate
            </div>
            <h2 className="text-3xl lg:text-5xl font-extrabold tracking-tight" data-testid="launch-final-heading">
              Stop Renting Attention.<br /><span className="text-[#2E6BFF]">Start Owning Revenue.</span>
            </h2>
            <p className="mt-5 text-sm lg:text-base text-[#E8EBF0]/60 max-w-xl mx-auto">
              Open a project brief in the terminal and lock in the $450 flat-rate special before this deployment window closes.
              A principal engineer responds within 24 hours — with numbers, not a sales deck.
            </p>
            <Link
              to="/terminal"
              data-testid="launch-final-cta-btn"
              className="mt-10 inline-flex items-center gap-2 bg-[#F59E0B] text-[#090C11] px-9 py-4 text-xs uppercase tracking-[0.22em] font-extrabold hover:bg-[#fbbf24] hover:shadow-[0_0_32px_rgba(245,158,11,0.45)] transition-all duration-300"
            >
              Claim the $450 Special <ArrowUpRight size={15} />
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[10px] uppercase tracking-[0.22em] text-[#E8EBF0]/40">
              <span className="inline-flex items-center gap-1.5"><Check size={12} className="text-[#10B981]" /> Fixed scope</span>
              <span className="inline-flex items-center gap-1.5"><Gauge size={12} className="text-[#10B981]" /> Sub-second loads</span>
              <span className="inline-flex items-center gap-1.5"><Lock size={12} className="text-[#10B981]" /> Hardened by default</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="border-t border-[#2E6BFF]/15 bg-[#0B0F16]" data-testid="launch-footer">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-center justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#E8EBF0]/35">© 2026 TraceElements · Commercial Revenue Engine</span>
          <div className="flex items-center gap-6">
            <Link to="/work" className="text-[10px] uppercase tracking-[0.25em] text-[#E8EBF0]/45 hover:text-[#2E6BFF] transition-colors" data-testid="launch-footer-work">Client Work</Link>
            <Link to="/pricing" className="text-[10px] uppercase tracking-[0.25em] text-[#E8EBF0]/45 hover:text-[#2E6BFF] transition-colors" data-testid="launch-footer-pricing">Retainers</Link>
            <Link to="/terminal" className="text-[10px] uppercase tracking-[0.25em] text-[#E8EBF0]/45 hover:text-[#2E6BFF] transition-colors" data-testid="launch-footer-terminal">Terminal</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
